import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { DARK_MODE } from '../../constants';
import {
	getPartValidationDetailsForSerialNumber,
	revalidatePart,
	updatePartValidation,
	validatePart,
} from '../../redux/actionCreators/partValidation';
import { createDefect } from '../../redux/actionCreators/qualityDefect';
import ModalDialog from '../../components/ModalDialog/ModalDialog';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';

const mapStateToProps = (state) => ({
	receivePart: state.receivePart,
	partValidation: state.partValidation,
	themeMode: state.theme.themeMode,
	user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
	getPartValidationDetailsForSerialNumber: (
		successCallback,
		errorCallback,
		sequenceNumber,
	) =>
		dispatch(
			getPartValidationDetailsForSerialNumber(
				successCallback,
				errorCallback,
				sequenceNumber,
			),
		),
	updatePartValidation: (data) => dispatch(updatePartValidation(data)),
	revalidatePart: (
		sequenceNumber,
		idToUpdate,
		payload,
		successCallback,
		errorCallback,
	) =>
		dispatch(
			revalidatePart(
				sequenceNumber,
				idToUpdate,
				payload,
				successCallback,
				errorCallback,
			),
		),
	validatePart: (sequenceNumber, payload, successCallback, errorCallback) =>
		dispatch(
			validatePart(sequenceNumber, payload, successCallback, errorCallback),
		),
	createDefect: (payload, successCallback, errorCallback) =>
		dispatch(createDefect(payload, successCallback, errorCallback)),
});

const DefectModal = ({
	themeMode,
	open,
	handleClose,
	partDetailsRow,
	partDetails,
	user,
	createDefect: createDefectAction,
	onDefectCreated,
}) => {
	const { showMessageOverlay } = useMessageContextManager();
	const isDark = themeMode === DARK_MODE;

	const [defectType, setDefectType] = useState('');
	const [remark, setRemark] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const serialNumber =
		partDetails?.serialNumber ??
		partDetailsRow?.[0]?.serialNumber;
	const sequenceNumber =
		partDetails?.sequenceNumber ??
		partDetailsRow?.[0]?.sequenceNumber;

	useEffect(() => {
		if (open) {
			setDefectType('');
			setRemark('');
			setSubmitting(false);
		}
	}, [open]);

	const handleCancel = () => {
		if (!submitting) {
			setDefectType('');
			setRemark('');
			handleClose();
		}
	};

	const handleSubmitDefect = () => {
		if (!serialNumber || !sequenceNumber) {
			showMessageOverlay({
				error: true,
				contextText: 'Add Defect',
				info: 'Serial number and sequence number are required.',
			});
			return;
		}
		const trimmedType = (defectType || '').trim();
		const trimmedRemark = (remark || '').trim();
		if (!trimmedType) {
			showMessageOverlay({
				error: true,
				contextText: 'Add Defect',
				info: 'Defect type is required.',
			});
			return;
		}
		setSubmitting(true);
		const closeOverlay = showMessageOverlay({
			contextText: 'Creating defect...',
			loading: true,
		});
		createDefectAction(
			{
				serialNumber,
				sequenceNumber,
				reason: trimmedType,
				remark: trimmedRemark,
				raisedBy: user?.username || '',
			},
			(data) => {
				setSubmitting(false);
				closeOverlay();
				showMessageOverlay({
					contextText: data?.message || 'Defect created successfully',
					success: true,
				});
				setDefectType('');
				setRemark('');
				handleClose();
				onDefectCreated?.();
			},
			(err) => {
				setSubmitting(false);
				closeOverlay();
				showMessageOverlay({
					error: true,
					contextText: 'Add Defect Failed',
					info: err?.response?.data?.message || 'Failed to create defect.',
				});
			},
		);
	};

	const btnSx = {
		minHeight: 64,
		minWidth: 180,
		px: 5,
		fontSize: '1.15rem',
		fontWeight: 700,
		textTransform: 'none',
		borderRadius: 2,
	};

	return (
		<ModalDialog open={open} title="Add Defect" onClose={handleCancel}>
			<Box sx={{ width: '100%', maxWidth: 620, mx: 'auto', px: { xs: 1, sm: 2 } }}>
				<Box
					sx={{
						mb: 3,
						p: 2,
						borderRadius: 2,
						backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
					}}
				>
					<Typography
						variant="body1"
						sx={{
							fontSize: '1.1rem',
							color: isDark ? '#e0e0e0' : '#424242',
							mb: 0.5,
						}}
					>
						<strong>VIN :</strong> {serialNumber || '—'}
					</Typography>
					{/* <Typography variant="body2" sx={{ color: isDark ? '#e0e0e0' : '#424242' }}>
						<strong>Sequence:</strong> {sequenceNumber || '—'}
					</Typography> */}
				</Box>

				<Grid container spacing={2.5}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Defect type"
							value={defectType}
							onChange={(e) => {
								const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').toUpperCase();
								setDefectType(value);
							}}
							placeholder="e.g. scratch"
							helperText="Short label for the defect (e.g. scratch, dent, loose wiring)"
							required
							disabled={submitting}
							inputProps={{ maxLength: 500 }}
							InputLabelProps={{ shrink: true }}
							sx={{
								'& .MuiInputBase-root': {
									fontSize: '1.1rem',
									minHeight: 60,
								},
								'& .MuiInputLabel-root': {
									fontSize: '1.2rem',
									fontWeight: 500,
								},
								'& .MuiFormHelperText-root': {
									fontSize: '0.95rem',
								},
							}}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Remark"
							value={remark}
							// onChange={(e) => setRemark(e.target.value)}
							onChange={(e) => {
								const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').toUpperCase();
								setRemark(value);
							}}
							placeholder="e.g. Detected during inspection"
							multiline
							rows={4}
							disabled={submitting}
							inputProps={{ maxLength: 1000 }}
							InputLabelProps={{ shrink: true }}
							sx={{
								'& .MuiInputBase-root': {
									fontSize: '1.1rem',
									minHeight: 60,
								},
								'& .MuiInputLabel-root': {
									fontSize: '1.2rem',
									fontWeight: 500,
								},
								'& .MuiFormHelperText-root': {
									fontSize: '0.95rem',
								},
							}}
						/>
					</Grid>
				</Grid>

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 2,
						mt: 4,
						mb: 1,
						flexWrap: 'wrap',
					}}
				>
					<Button
						variant="outlined"
						size="large"
						onClick={handleCancel}
						disabled={submitting}
						sx={{
							...btnSx,
							borderColor: isDark ? '#ffffff' : '#616161',
							color: isDark ? '#ffffff' : '#424242',
							backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#f5f5f5',
							'&:hover': {
								borderColor: isDark ? '#ffffff' : '#212121',
								backgroundColor: isDark ? 'rgba(255,255,255,0.14)' : '#e0e0e0',
							},
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						size="large"
						disabled={submitting || !serialNumber || !sequenceNumber}
						onClick={handleSubmitDefect}
						sx={{
							...btnSx,
							minWidth: 180,
							background: 'linear-gradient(90deg, #BD0020 0%, #FF2742 100%)',
							'&:hover': {
								background: 'linear-gradient(90deg, #A0001A 0%, #E02038 100%)',
							},
						}}
					>
						{submitting ? 'Submitting…' : 'Submit'}
					</Button>
				</Box>
			</Box>
		</ModalDialog>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(DefectModal);
